const TYPES = {
  char: [1, "getInt8"], int8: [1, "getInt8"], uchar: [1, "getUint8"], uint8: [1, "getUint8"],
  short: [2, "getInt16"], int16: [2, "getInt16"], ushort: [2, "getUint16"], uint16: [2, "getUint16"],
  int: [4, "getInt32"], int32: [4, "getInt32"], uint: [4, "getUint32"], uint32: [4, "getUint32"],
  float: [4, "getFloat32"], float32: [4, "getFloat32"], double: [8, "getFloat64"], float64: [8, "getFloat64"],
};
const SH_C0 = 0.28209479177387814;
export const TEX_W = 4096;

function findHeaderEnd(bytes) {
  const m = [101, 110, 100, 95, 104, 101, 97, 100, 101, 114]; // "end_header"
  for (let i = 0; i < Math.min(bytes.length, 65536); i++) {
    let ok = true;
    for (let j = 0; j < m.length; j++) if (bytes[i + j] !== m[j]) { ok = false; break; }
    if (ok) {
      let e = i + m.length;
      while (bytes[e] !== 10) e++;
      return e + 1;
    }
  }
  throw new Error("PLY header not found");
}

export async function loadPly(url) {
  const buf = await (await fetch(url)).arrayBuffer();
  const bytes = new Uint8Array(buf);
  const end = findHeaderEnd(bytes);
  const header = new TextDecoder().decode(bytes.subarray(0, end));
  if (!/format binary_little_endian/.test(header)) throw new Error("Only binary little-endian PLY files are supported");
  let el = null;
  let count = 0;
  let faces = 0;
  const props = [];
  for (const line of header.split("\n")) {
    const t = line.trim().split(/\s+/);
    if (t[0] === "element") { el = t[1]; if (el === "vertex") count = +t[2]; if (el === "face") faces = +t[2]; }
    if (t[0] === "property" && el === "vertex" && t[1] !== "list") props.push({ name: t[2], type: t[1] });
  }
  let stride = 0;
  for (const p of props) { p.offset = stride; stride += TYPES[p.type][0]; }
  const view = new DataView(buf, end);
  const data = {};
  for (const p of props) {
    const arr = new Float32Array(count);
    const fn = TYPES[p.type][1];
    for (let i = 0; i < count; i++) arr[i] = view[fn](i * stride + p.offset, true);
    data[p.name] = arr;
  }
  return { count, faces, data };
}

const has = (d, ...k) => k.every((n) => d[n]);

export function buildSplats({ count, faces, data: d }) {
  const report = {
    vertices: count,
    faces,
    colors: has(d, "f_dc_0", "f_dc_1", "f_dc_2") ? "spherical-harmonic DC" : has(d, "red", "green", "blue") ? "rgb" : "missing",
    opacity: !!d.opacity,
    scales: has(d, "scale_0", "scale_1", "scale_2"),
    rotations: has(d, "rot_0", "rot_1", "rot_2", "rot_3"),
    normals: has(d, "nx", "ny", "nz") && d.nx.some((v) => v !== 0),
  };
  const idx = [];
  for (let i = 0; i < count; i++) {
    const a = d.opacity ? 1 / (1 + Math.exp(-d.opacity[i])) : 1;
    if (a > 0.02) idx.push(i);
  }
  const N = idx.length;
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  for (const i of idx) {
    const p = [d.x[i], d.y[i], d.z[i]];
    for (let k = 0; k < 3; k++) { min[k] = Math.min(min[k], p[k]); max[k] = Math.max(max[k], p[k]); }
  }
  const ctr = min.map((v, k) => (v + max[k]) / 2);
  const span = Math.max(max[0] - min[0], max[1] - min[1], max[2] - min[2]);
  const k = 1 / span;
  const H = Math.ceil((N * 4) / TEX_W);
  const tex = new Float32Array(TEX_W * H * 4);
  const centers = new Float32Array(N * 3);

  idx.forEach((i, n) => {
    const x = (d.x[i] - ctr[0]) * k, y = (d.y[i] - ctr[1]) * k, z = (d.z[i] - ctr[2]) * k;
    centers.set([x, y, z], n * 3);
    let r, g, b;
    if (report.colors === "spherical-harmonic DC") { r = 0.5 + SH_C0 * d.f_dc_0[i]; g = 0.5 + SH_C0 * d.f_dc_1[i]; b = 0.5 + SH_C0 * d.f_dc_2[i]; }
    else if (report.colors === "rgb") { r = d.red[i] / 255; g = d.green[i] / 255; b = d.blue[i] / 255; }
    else { r = g = b = 0.3; }
    const alpha = d.opacity ? 1 / (1 + Math.exp(-d.opacity[i])) : 1;
    const s = report.scales ? [Math.exp(d.scale_0[i]) * k, Math.exp(d.scale_1[i]) * k, Math.exp(d.scale_2[i]) * k] : [0.004, 0.004, 0.004];
    let [qw, qx, qy, qz] = report.rotations ? [d.rot_0[i], d.rot_1[i], d.rot_2[i], d.rot_3[i]] : [1, 0, 0, 0];
    const ql = Math.hypot(qw, qx, qy, qz) || 1;
    qw /= ql; qx /= ql; qy /= ql; qz /= ql;
    const R = [
      1 - 2 * (qy * qy + qz * qz), 2 * (qx * qy - qw * qz), 2 * (qx * qz + qw * qy),
      2 * (qx * qy + qw * qz), 1 - 2 * (qx * qx + qz * qz), 2 * (qy * qz - qw * qx),
      2 * (qx * qz - qw * qy), 2 * (qy * qz + qw * qx), 1 - 2 * (qx * qx + qy * qy),
    ];
    const M = R.map((v, j) => v * s[j % 3]);
    const cov = (a, c) => M[a * 3] * M[c * 3] + M[a * 3 + 1] * M[c * 3 + 1] + M[a * 3 + 2] * M[c * 3 + 2];
    const order = [0, 1, 2].sort((a, c) => s[a] - s[c]);
    const flat = 1 - s[order[0]] / s[order[1]];
    const nrm = report.normals
      ? [d.nx[i], d.ny[i], d.nz[i]]
      : [R[order[0]] * flat, R[3 + order[0]] * flat, R[6 + order[0]] * flat];
    const o = n * 16;
    tex.set([x, y, z, nrm[0], Math.min(Math.max(r, 0), 1), Math.min(Math.max(g, 0), 1), Math.min(Math.max(b, 0), 1), alpha,
      cov(0, 0), cov(0, 1), cov(0, 2), cov(1, 1), cov(1, 2), cov(2, 2), nrm[1], nrm[2]], o);
  });
  return { count: N, tex, texHeight: H, centers, report };
}
